import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PatientAvatarUpload from './PatientAvatarUpload';
import {
  PATIENT_PANEL_THEME,
  PATIENT_PANEL_HEADER_BLEED,
  patientPanelActionButtonSx,
  patientPanelBackBarSx,
  patientPanelBackButtonSx,
  patientPanelBackLabelSx,
  patientPanelCloseButtonSx,
  patientPanelHeaderSx,
  patientPanelIconButtonSx,
  patientPanelPrimaryActionButtonSx,
  patientPanelQuickActionButtonSx,
  patientPanelTabsBarSx,
} from './patientPanelTheme';

export interface PatientPanelHeaderAction {
  key: string;
  label: string;
  tooltip?: string;
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
  /** Ação principal: botão cheio, mantém o texto mesmo no cabeçalho recolhido. */
  primary?: boolean;
  /** Mobile: botão com nome visível na faixa (ex.: Receituário, Orçamento). */
  showToolbarLabel?: boolean;
  danger?: boolean;
}

interface PatientPanelHeaderProps {
  patientId: string;
  fullName: string;
  avatarUrl?: string;
  onAvatarUpdated: (url: string) => void;
  isMobile: boolean;
  /** Cabeçalho recolhido (conteúdo rolado): só identidade mínima, ações e abas. */
  collapsed: boolean;
  backLabel?: string;
  onBack?: () => void;
  appointmentNotes?: string;
  /** Status, plano e sessão paga — chips que pedem atenção. */
  badges?: React.ReactNode;
  /** Informações em texto corrido: idade, convênio, 1ª consulta… */
  metaItems: string[];
  diagnosis?: { label?: string | null; needsSelection: boolean; onSelect: () => void } | null;
  tags?: React.ReactNode;
  actions: PatientPanelHeaderAction[];
  /** Botões extras (ex.: Memed) renderizados após as ações principais. */
  extraHeaderActions?: React.ReactNode;
  menuActions: PatientPanelHeaderAction[];
  onEdit?: () => void;
  onRefresh?: () => void;
  onClose?: () => void;
  tabs: React.ReactNode;
}

const metaTextSx = {
  fontSize: '0.75rem',
  color: PATIENT_PANEL_THEME.slate,
  lineHeight: 1.4,
} as const;

function isMobileToolbarLabeledAction(action: PatientPanelHeaderAction): boolean {
  return Boolean(action.primary || action.showToolbarLabel);
}

const PatientPanelHeader: React.FC<PatientPanelHeaderProps> = ({
  patientId,
  fullName,
  avatarUrl,
  onAvatarUpdated,
  isMobile,
  collapsed,
  backLabel,
  onBack,
  appointmentNotes,
  badges,
  metaItems,
  diagnosis,
  tags,
  actions,
  extraHeaderActions,
  menuActions,
  onEdit,
  onRefresh,
  onClose,
  tabs,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const notes = String(appointmentNotes || '').trim();
  const iconOnlyActions = isMobile || collapsed;

  const mobileInlineActions = useMemo(
    () => (isMobile ? actions.filter(isMobileToolbarLabeledAction) : actions),
    [isMobile, actions],
  );

  const mobileOverflowActions = useMemo(() => {
    if (!isMobile) return menuActions;
    return [
      ...actions.filter((action) => !isMobileToolbarLabeledAction(action)),
      ...menuActions,
    ];
  }, [isMobile, actions, menuActions]);

  const headerToolbarBleedSx = {
    mx: PATIENT_PANEL_HEADER_BLEED,
    px: { xs: 0.5, sm: 0.75 },
  } as const;

  const renderAction = (action: PatientPanelHeaderAction) => {
    const showLabel = !iconOnlyActions || isMobileToolbarLabeledAction(action);
    const button = showLabel ? (
      <Button
        size="small"
        variant={action.primary ? 'contained' : 'outlined'}
        disableElevation
        startIcon={action.icon}
        onClick={action.onClick}
        disabled={action.disabled}
        sx={
          action.primary
            ? patientPanelPrimaryActionButtonSx
            : action.showToolbarLabel
              ? patientPanelQuickActionButtonSx
              : patientPanelActionButtonSx
        }
      >
        {action.label}
      </Button>
    ) : (
      <IconButton
        size="small"
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={action.label}
        sx={patientPanelIconButtonSx}
      >
        {action.icon}
      </IconButton>
    );
    return (
      <Tooltip key={action.key} title={action.tooltip || action.label}>
        <span>{button}</span>
      </Tooltip>
    );
  };

  return (
    <Box
      className="patient-panel-header"
      sx={{
        ...patientPanelHeaderSx,
        ...(isMobile
          ? {
              backgroundImage: 'none',
              bgcolor: PATIENT_PANEL_THEME.headerBg,
            }
          : {}),
      }}
    >
      {onBack && (
        <Box
          sx={{
            ...patientPanelBackBarSx,
            ...(isMobile
              ? {
                  mt: 0,
                  mx: 0,
                  px: 0,
                  bgcolor: PATIENT_PANEL_THEME.headerBg,
                  position: 'relative',
                  zIndex: 2,
                }
              : {}),
          }}
        >
          <IconButton size="small" onClick={onBack} aria-label={backLabel} sx={patientPanelBackButtonSx}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography component="button" type="button" onClick={onBack} sx={patientPanelBackLabelSx}>
            {backLabel}
          </Typography>
        </Box>
      )}

      {notes && !collapsed && (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setNotesExpanded((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setNotesExpanded((v) => !v); }}
          title={notesExpanded ? 'Recolher' : 'Ver observação completa'}
          sx={{
            mb: 0.75,
            py: 0.25,
            px: 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            bgcolor: PATIENT_PANEL_THEME.muted,
            border: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
            fontSize: '0.68rem',
            lineHeight: 1.4,
            color: PATIENT_PANEL_THEME.slate,
            ...(notesExpanded
              ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 96, overflowY: 'auto' }
              : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: PATIENT_PANEL_THEME.slateMid, mr: 0.5 }}>
            Obs. consulta:
          </Box>
          {notes}
        </Box>
      )}

      {isMobile ? (
        <>
          <Box sx={{ display: 'flex', alignItems: collapsed ? 'center' : 'flex-start', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ flexShrink: 0 }}>
              <PatientAvatarUpload
                patientId={patientId}
                currentAvatarUrl={avatarUrl}
                patientName={fullName}
                size={collapsed ? 28 : 40}
                onAvatarUpdated={onAvatarUpdated}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Tooltip title={fullName} placement="bottom-start" enterDelay={500}>
                  <Typography
                    component="div"
                    noWrap
                    sx={{
                      flex: '1 1 40%',
                      minWidth: 0,
                      fontWeight: 800,
                      color: PATIENT_PANEL_THEME.navy,
                      lineHeight: 1.3,
                      fontSize: collapsed ? '0.92rem' : '0.98rem',
                    }}
                  >
                    {fullName}
                  </Typography>
                </Tooltip>
                {!collapsed && tags ? (
                  <Box sx={{ flex: '1 1 45%', minWidth: 56, maxWidth: '48%', display: 'flex', justifyContent: 'flex-end' }}>
                    {tags}
                  </Box>
                ) : null}
              </Box>

              <Collapse in={!collapsed} timeout={160}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.75, rowGap: 0.5, mt: 0.35 }}>
                  {badges}
                  {metaItems.length > 0 && (
                    <Typography component="span" sx={metaTextSx}>
                      {metaItems.join(' · ')}
                    </Typography>
                  )}
                  {diagnosis?.label ? (
                    <Typography component="span" sx={{ ...metaTextSx, fontWeight: 600, color: PATIENT_PANEL_THEME.slateMid }}>
                      {metaItems.length > 0 ? '· ' : ''}Diagnóstico: {diagnosis.label}
                    </Typography>
                  ) : diagnosis?.needsSelection ? (
                    <Button
                      size="small"
                      color="warning"
                      onClick={diagnosis.onSelect}
                      sx={{ p: 0, minWidth: 0, fontWeight: 600, fontSize: '0.75rem', textTransform: 'none', lineHeight: 1.4 }}
                    >
                      Selecione o diagnóstico principal
                    </Button>
                  ) : null}
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Box
            sx={{
              ...headerToolbarBleedSx,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'nowrap',
              mt: collapsed ? 0.5 : 0.65,
              justifyContent: 'flex-start',
              width: '100%',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {mobileInlineActions.map(renderAction)}
            {extraHeaderActions}
            {mobileOverflowActions.length > 0 && (
              <Tooltip title="Mais ações">
                <IconButton
                  size="small"
                  aria-label="Mais ações"
                  aria-haspopup="menu"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={patientPanelIconButtonSx}
                >
                  <MoreIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Editar ficha do paciente">
                <IconButton size="small" onClick={onEdit} aria-label="Editar ficha do paciente" sx={patientPanelIconButtonSx}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {onRefresh && !collapsed && (
              <Tooltip title="Atualizar dados">
                <IconButton size="small" onClick={onRefresh} aria-label="Atualizar dados" sx={patientPanelIconButtonSx}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="Fechar painel" sx={patientPanelCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexWrap: 'wrap', rowGap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: collapsed ? 'center' : 'flex-start', gap: 1.25, flex: '1 1 240px', minWidth: 0 }}>
            <Box sx={{ flexShrink: 0 }}>
              <PatientAvatarUpload
                patientId={patientId}
                currentAvatarUrl={avatarUrl}
                patientName={fullName}
                size={collapsed ? 28 : 40}
                onAvatarUpdated={onAvatarUpdated}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={fullName} placement="bottom-start" enterDelay={500}>
                <Typography
                  component="div"
                  noWrap
                  sx={{
                    fontWeight: 800,
                    color: PATIENT_PANEL_THEME.navy,
                    lineHeight: 1.3,
                    fontSize: collapsed ? '0.92rem' : { xs: '0.98rem', sm: '1.05rem' },
                  }}
                >
                  {fullName}
                </Typography>
              </Tooltip>

              <Collapse in={!collapsed} timeout={160}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.75, rowGap: 0.5, mt: 0.35 }}>
                  {badges}
                  {metaItems.length > 0 && (
                    <Typography component="span" sx={metaTextSx}>
                      {metaItems.join(' · ')}
                    </Typography>
                  )}
                  {diagnosis?.label ? (
                    <Typography component="span" sx={{ ...metaTextSx, fontWeight: 600, color: PATIENT_PANEL_THEME.slateMid }}>
                      {metaItems.length > 0 ? '· ' : ''}Diagnóstico: {diagnosis.label}
                    </Typography>
                  ) : diagnosis?.needsSelection ? (
                    <Button
                      size="small"
                      color="warning"
                      onClick={diagnosis.onSelect}
                      sx={{ p: 0, minWidth: 0, fontWeight: 600, fontSize: '0.75rem', textTransform: 'none', lineHeight: 1.4 }}
                    >
                      Selecione o diagnóstico principal
                    </Button>
                  ) : null}
                  {tags}
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
            {actions.map(renderAction)}
            {extraHeaderActions}
            {menuActions.length > 0 && (
              <Tooltip title="Mais ações">
                <IconButton
                  size="small"
                  aria-label="Mais ações"
                  aria-haspopup="menu"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={patientPanelIconButtonSx}
                >
                  <MoreIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Editar ficha do paciente">
                <IconButton size="small" onClick={onEdit} aria-label="Editar ficha do paciente" sx={patientPanelIconButtonSx}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {onRefresh && !collapsed && (
              <Tooltip title="Atualizar dados">
                <IconButton size="small" onClick={onRefresh} aria-label="Atualizar dados" sx={patientPanelIconButtonSx}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="Fechar painel" sx={patientPanelCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220, borderRadius: 2 } } }}
      >
        {(isMobile ? mobileOverflowActions : menuActions).map((action) => (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              setMenuAnchor(null);
              action.onClick();
            }}
            sx={{ fontSize: '0.85rem', ...(action.danger ? { color: '#dc2626' } : {}) }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: action.danger ? '#dc2626' : PATIENT_PANEL_THEME.tealDark, '& svg': { fontSize: 18 } }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText
              primary={action.label}
              secondary={action.disabled && action.tooltip ? action.tooltip : undefined}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
              secondaryTypographyProps={{ fontSize: '0.7rem' }}
            />
          </MenuItem>
        ))}
      </Menu>

      <Box sx={patientPanelTabsBarSx}>{tabs}</Box>
    </Box>
  );
};

export default PatientPanelHeader;
